/**
 * La petite table — widget de tira-dúvidas (chatbot roteirizado por botões).
 * Sem texto livre e sem LLM → não alucina. Espelha o do Sol, adaptado ao
 * produto self-service: a "saída" leva ao teste grátis (não a um formulário).
 */
import { useState } from 'react';

import { FAQ, type ChatLink, type LinkTo, type Locale } from './faq';
import { LOCALE_PATHS, PARTNER_PATHS } from '../lib/seo';
import { track } from '../lib/analytics';

function resolveLink(to: LinkTo, lang: string): string {
  switch (to) {
    case 'trial': return (LOCALE_PATHS[lang] ?? '/') === '/' ? '/#demo' : `${LOCALE_PATHS[lang]}#demo`;
    case 'pricing': return (LOCALE_PATHS[lang] ?? '/') === '/' ? '/#pricing' : `${LOCALE_PATHS[lang]}#pricing`;
    case 'partners': return PARTNER_PATHS[lang] ?? '/parceiros';
  }
}

type View = { kind: 'topics' } | { kind: 'category'; id: string } | { kind: 'answer'; id: string };

export function ChatWidget({ lang }: { lang: string }) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>({ kind: 'topics' });

  const copy = FAQ[(lang as Locale)] ?? FAQ['pt-BR'];

  function LinkBtns({ links }: { links?: ChatLink[] }) {
    if (!links?.length) return null;
    return (
      <div className="lc-links">
        {links.map((l) => (
          <a key={l.to} className="lc-link" href={resolveLink(l.to, lang)} onClick={() => track('chat_cta', { to: l.to })}>{l.label} →</a>
        ))}
      </div>
    );
  }

  return (
    <>
      {!open && (
        <button className="lc-launcher" aria-label={copy.launcher} title={copy.launcher} onClick={() => { setOpen(true); setView({ kind: 'topics' }); track('chat_open', { lang }); }}>
          <span aria-hidden>💬</span>
          <span className="lc-launcher-tx">{copy.launcher}</span>
        </button>
      )}

      {open && (
        <div className="lc-panel" role="dialog" aria-label={copy.title}>
          <header className="lc-head">
            <div className="lc-head-tx">
              <strong>{copy.title}</strong>
              <span>{copy.subtitle}</span>
            </div>
            <button className="lc-x" aria-label="×" onClick={() => setOpen(false)}>×</button>
          </header>

          <div className="lc-body">
            <div className="lc-bot">{copy.welcome}</div>

            {view.kind === 'topics' && (
              <div className="lc-grid">
                {copy.categories.map((c) => (
                  <button key={c.id} className="lc-topic" onClick={() => { setView({ kind: 'category', id: c.id }); track('chat_topic', { topic: c.id }); }}>
                    <span aria-hidden>{c.icon}</span>
                    <span>{c.label}</span>
                  </button>
                ))}
              </div>
            )}

            {view.kind === 'category' && (() => {
              const cat = copy.categories.find((c) => c.id === view.id)!;
              return (
                <div className="lc-list">
                  <button className="lc-back" onClick={() => setView({ kind: 'topics' })}>← {copy.backToTopics}</button>
                  <div className="lc-bot lc-bot-sub">{cat.icon} {cat.label}</div>
                  {cat.questions.map((qid) => (
                    <button key={qid} className="lc-q" onClick={() => { setView({ kind: 'answer', id: qid }); track('chat_question', { q: qid }); }}>{copy.answers[qid].q}</button>
                  ))}
                </div>
              );
            })()}

            {view.kind === 'answer' && (() => {
              const ans = copy.answers[view.id];
              return (
                <div className="lc-answer">
                  <button className="lc-back" onClick={() => setView({ kind: 'topics' })}>← {copy.backToTopics}</button>
                  <div className="lc-q-echo">{ans.q}</div>
                  <div className="lc-bot">
                    {ans.a.split('\n').map((p, i) => <p key={i}>{p}</p>)}
                    <LinkBtns links={ans.links} />
                  </div>
                  {!!ans.next?.length && (
                    <div className="lc-related">
                      <span className="lc-related-h">{copy.related}</span>
                      {ans.next.map((nid) => (
                        <button key={nid} className="lc-q lc-q-sm" onClick={() => setView({ kind: 'answer', id: nid })}>{copy.answers[nid].q}</button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="lc-foot-cta">
              <span>{copy.notFound}</span>
              <a className="lc-cta" href={resolveLink('trial', lang)}>{copy.notFoundCta} →</a>
            </div>
          </div>

          <footer className="lc-note">{copy.note}</footer>
        </div>
      )}
    </>
  );
}
